import { useEffect, useCallback, useRef, useState, useMemo } from 'react'
import {
  Title,
  Stack,
  Group,
  Select,
  Alert,
  Badge,
  Text,
  Modal,
  Button,
  Textarea,
  Divider,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { DataTable } from 'mantine-datatable'
import { IconAlertCircle, IconDatabaseOff, IconCheck, IconX } from '@tabler/icons-react'
import { useAccessRequestsStore } from '../../stores/accessRequestsStore'
import {
  approveAccessRequest,
  denyAccessRequest,
  getErrorMessage,
} from '../../api/accessRequestsApi'
import { listApplications } from '../../api/applicationsApi'
import type { Application } from '../../api/applicationsApi'
import type { AccessRequest, AccessRequestStatus } from '../../api/accessRequestsApi'

const PAGE_SIZES = [10, 20, 50, 100]

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
]

const STATUS_COLORS: Record<AccessRequestStatus, string> = {
  pending: 'yellow',
  approved: 'green',
  denied: 'red',
}

function formatDate(date: Date | string | undefined): string {
  if (!date) return '—'
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getName(request: AccessRequest): string {
  const { firstName, lastName } = request.profile
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ')
  }
  return '—'
}

export function PendingUsersView() {
  const {
    requests,
    total,
    page,
    pageSize,
    isLoading,
    error,
    statusFilter,
    fetchRequests,
    setPage,
    setPageSize,
    setStatusFilter,
    clearError,
  } = useAccessRequestsStore()

  // Applications lookup for displaying names and allowed roles
  const [applications, setApplications] = useState<Application[]>([])
  const appNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const app of applications) {
      map[app.appId] = app.name
    }
    return map
  }, [applications])

  const appRolesMap = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const app of applications) {
      map[app.appId] = app.allowedRoles
    }
    return map
  }, [applications])

  const appDefaultRoleMap = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    for (const app of applications) {
      map[app.appId] = app.defaultNewUserRole
    }
    return map
  }, [applications])

  // Modal state
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [denyReason, setDenyReason] = useState('')
  const [showDenyForm, setShowDenyForm] = useState(false)
  const [appRoles, setAppRoles] = useState<Record<string, string>>({})

  // Fetch applications on mount
  useEffect(() => {
    listApplications(true).then((res) => setApplications(res.applications))
  }, [])

  // Initial fetch on mount only
  const fetchRequestsRef = useRef(fetchRequests)
  fetchRequestsRef.current = fetchRequests

  useEffect(() => {
    fetchRequestsRef.current()
  }, [])

  const handleStatusChange = useCallback(
    (value: string | null) => {
      setStatusFilter((value as AccessRequestStatus) || null)
    },
    [setStatusFilter]
  )

  const handleRowClick = useCallback(
    (request: AccessRequest) => {
      setSelectedRequest(request)
      setShowDenyForm(false)
      setDenyReason('')
      const initialRoles: Record<string, string> = {}
      for (const appId of request.requestedApps) {
        const defaultRole = appDefaultRoleMap[appId]
        if (defaultRole) {
          initialRoles[appId] = defaultRole
        }
      }
      setAppRoles(initialRoles)
      openModal()
    },
    [openModal, appDefaultRoleMap]
  )

  const handleApprove = async () => {
    if (!selectedRequest) return

    setActionLoading(true)
    try {
      await approveAccessRequest(selectedRequest._id, {
        apps: selectedRequest.requestedApps.map((appId) => ({
          appId,
          role: appRoles[appId] ?? appDefaultRoleMap[appId] ?? '',
        })),
      })
      notifications.show({
        title: 'Request Approved',
        message: `Access granted to ${selectedRequest.email}`,
        color: 'green',
      })
      closeModal()
      fetchRequests()
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeny = async () => {
    if (!selectedRequest || !denyReason.trim()) return

    setActionLoading(true)
    try {
      await denyAccessRequest(selectedRequest._id, { reason: denyReason })
      notifications.show({
        title: 'Request Denied',
        message: `Access denied for ${selectedRequest.email}`,
        color: 'orange',
      })
      closeModal()
      fetchRequests()
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const getAppName = (appId: string) => appNameMap[appId] || appId

  const formatRequestedApps = (apps: string[]): string => {
    if (!apps || apps.length === 0) return '—'
    const names = apps.map(getAppName)
    if (names.length <= 2) return names.join(', ')
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Pending Users</Title>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={clearError}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Group>
        <Select
          placeholder="Filter by status"
          data={STATUS_OPTIONS}
          value={statusFilter || ''}
          onChange={handleStatusChange}
          clearable={false}
          style={{ width: 160 }}
        />
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        idAccessor="_id"
        records={requests}
        fetching={isLoading}
        minHeight={200}
        noRecordsIcon={<IconDatabaseOff size={24} strokeWidth={1.5} />}
        noRecordsText="No access requests found"
        onRowClick={({ record }) => handleRowClick(record)}
        style={{ cursor: 'pointer' }}
        columns={[
          {
            accessor: 'email',
            title: 'Email',
            width: 250,
          },
          {
            accessor: 'name',
            title: 'Name',
            width: 150,
            render: (request) => getName(request),
          },
          {
            accessor: 'organization',
            title: 'Organization',
            width: 150,
            render: (request) => (
              <Text size="sm" c="dimmed">
                {request.profile.organization || '—'}
              </Text>
            ),
          },
          {
            accessor: 'requestedApps',
            title: 'Requested Apps',
            width: 180,
            render: (request) => (
              <Text size="sm" c="dimmed">
                {formatRequestedApps(request.requestedApps)}
              </Text>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            width: 100,
            render: (request) => (
              <Badge color={STATUS_COLORS[request.status]} variant="light">
                {request.status}
              </Badge>
            ),
          },
          {
            accessor: 'createdAt',
            title: 'Submitted',
            width: 120,
            render: (request) => (
              <Text size="sm" c="dimmed">
                {formatDate(request.createdAt)}
              </Text>
            ),
          },
        ]}
        totalRecords={total}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={setPageSize}
        paginationText={({ from, to, totalRecords }) =>
          `${from}-${to} of ${totalRecords} requests`
        }
      />

      {/* Request Detail / Approve / Deny Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={<Text fw={600}>Access Request</Text>}
        size="md"
      >
        {selectedRequest && (
          <Stack gap="md">
            <div>
              <Text size="sm" c="dimmed" mb={4}>
                Email
              </Text>
              <Text>{selectedRequest.email}</Text>
            </div>

            <Group grow>
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Name
                </Text>
                <Text>{getName(selectedRequest)}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Status
                </Text>
                <Badge color={STATUS_COLORS[selectedRequest.status]} variant="light">
                  {selectedRequest.status}
                </Badge>
              </div>
            </Group>

            <Group grow>
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Organization
                </Text>
                <Text>{selectedRequest.profile.organization || '—'}</Text>
              </div>
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Job Title
                </Text>
                <Text>{selectedRequest.profile.jobTitle || '—'}</Text>
              </div>
            </Group>

            <div>
              <Text size="sm" c="dimmed" mb={8}>
                Requested Applications
              </Text>
              <Stack gap="xs">
                {selectedRequest.requestedApps.map((appId) => {
                  const allowed = appRolesMap[appId] ?? []
                  return (
                    <Group key={appId} justify="space-between" align="center">
                      <Text size="sm">{getAppName(appId)}</Text>
                      {selectedRequest.status === 'pending' ? (
                        <Select
                          size="xs"
                          placeholder="Select role"
                          value={appRoles[appId] ?? appDefaultRoleMap[appId] ?? null}
                          data={allowed.map((r) => ({ value: r, label: r }))}
                          onChange={(val) =>
                            setAppRoles((prev) => ({
                              ...prev,
                              [appId]: val ?? '',
                            }))
                          }
                          allowDeselect={false}
                          style={{ width: 130 }}
                        />
                      ) : (
                        <Badge variant="light">{getAppName(appId)}</Badge>
                      )}
                    </Group>
                  )
                })}
              </Stack>
            </div>

            <div>
              <Text size="sm" c="dimmed" mb={4}>
                Submitted
              </Text>
              <Text>{formatDate(selectedRequest.createdAt)}</Text>
            </div>

            {selectedRequest.status === 'pending' && (
              <>
                <Divider my="sm" />

                {showDenyForm ? (
                  <Stack gap="sm">
                    <Textarea
                      label="Denial Reason"
                      placeholder="Please provide a reason for denying this request..."
                      value={denyReason}
                      onChange={(e) => setDenyReason(e.currentTarget.value)}
                      minRows={3}
                      required
                    />
                    <Group justify="flex-end">
                      <Button
                        variant="default"
                        onClick={() => setShowDenyForm(false)}
                        disabled={actionLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="red"
                        onClick={handleDeny}
                        loading={actionLoading}
                        disabled={!denyReason.trim()}
                        leftSection={<IconX size={16} />}
                      >
                        Deny Request
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <Group justify="flex-end">
                    <Button
                      variant="light"
                      color="red"
                      onClick={() => setShowDenyForm(true)}
                      leftSection={<IconX size={16} />}
                    >
                      Deny
                    </Button>
                    <Button
                      color="green"
                      onClick={handleApprove}
                      loading={actionLoading}
                      leftSection={<IconCheck size={16} />}
                    >
                      Approve
                    </Button>
                  </Group>
                )}
              </>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
